import React from 'react';

// Modal imports
import KeyInformationModal from '../../review/KeyInformationModal';
import SectionAssignModal from '../../assignment/SectionAssignModal';
import DocumentInfoModal from './modals/DocumentInfoModal';
import StageConfirmationModal from '../../approval/StageConfirmationModal';
import AIImprovementModal from './modals/AIImprovementModal';
import DecisionModal from './modals/DecisionModal';
import ExtensionRequestModal from './modals/ExtensionRequestModal';
import RenameVersionModal from './modals/RenameVersionModal';
import RevertVersionModal from './modals/RevertVersionModal';

import { stripPrefix } from '../utils/helperUtils';

/**
 * RFPModalsGroup — extracted from RFPAnalysisResult.jsx (Phase 10 refactor).
 * Renders all workflow modals for the RFP Analysis page as a single group:
 *   KeyInformationModal, SectionAssignModal, DocumentInfoModal,
 *   StageConfirmationModal, AIImprovementModal, DecisionModal,
 *   ExtensionRequestModal, RenameVersionModal, RevertVersionModal.
 * Zero logic changes. All state/handlers passed as props.
 */
const RFPModalsGroup = ({
    setSuccessToast,
    // Key Info
    isKeyInfoModalOpen, setIsKeyInfoModalOpen,
    handleUpdateMetadata, isUpdating, editableData,
    handleMetadataChange, formatForDateInput,

    // Section Assign
    isAssignModalOpen, setIsAssignModalOpen,
    assignments, setAssignments, rfpId, teamMembers,
    fetchOverview, fetchRFPData,

    // Document Info
    isDocInfoModalOpen, setIsDocInfoModalOpen,
    documents, setDocuments,
    documentAssignments, setDocumentAssignments,

    // Stage Confirmation
    stageModal, setStageModal, stageError, setStageError,
    handleClarificationRequest, activeSection, executeStageTransition,
    isStageUpdating,

    // AI Improvement
    showAIModal, setShowAIModal,
    aiPrompt, setAiPrompt, handleAIImprove, isRefining,

    // Rename Version
    showLabelModal, setShowLabelModal,
    versionLabelText, setVersionLabelText, handleRenameVersion,

    // Revert Version
    showRevertModal, setShowRevertModal,
    revertTargetVersion, revertReason, setRevertReason, handleRevertVersion,

    // Decision (Extension Review by Lead)
    isDecisionModalOpen, setIsDecisionModalOpen,
    decisionSection, decisionDate, setDecisionDate, handleExtensionDecision,
    toDMY,

    // Extension Request (Team Member)
    isExtensionModalOpen, setIsExtensionModalOpen,
    selectedSection, extensionDate, setExtensionDate,
    extensionNotes, setExtensionNotes, handleRequestExtension,
    isManager,
    fetchAssignedUsersData,
    assignedUsers,
    isTeamLeadViewOnly = false
}) => (
    <>
        <KeyInformationModal
            isOpen={isKeyInfoModalOpen}
            onClose={() => setIsKeyInfoModalOpen(false)}
            onSave={async (payload) => {
                const success = await handleUpdateMetadata(payload);
                if (success) setIsKeyInfoModalOpen(false);
            }}
            isUpdating={isUpdating}
            editableData={editableData}
            handleMetadataChange={handleMetadataChange}
            formatForDateInput={formatForDateInput}
        />

        <SectionAssignModal
            isOpen={isAssignModalOpen}
            onClose={() => setIsAssignModalOpen(false)}
            assignments={assignments}
            setAssignments={setAssignments}
            rfpId={rfpId}
            editableData={editableData}
            teamMembers={teamMembers}
            fetchOverview={fetchOverview}
            fetchRFPData={fetchRFPData}
            setSuccessToast={setSuccessToast}
        />

        <DocumentInfoModal
            isOpen={isDocInfoModalOpen}
            onClose={() => setIsDocInfoModalOpen(false)}
            documents={documents}
            setDocuments={setDocuments}
            documentAssignments={documentAssignments}
            setDocumentAssignments={setDocumentAssignments}
            teamMembers={assignedUsers}
            rfpId={rfpId}
            isManager={isManager}
            fetchAssignedUsersData={fetchAssignedUsersData}
            isTeamLeadViewOnly={isTeamLeadViewOnly}
        />

        <StageConfirmationModal
            isOpen={stageModal.show}
            onClose={() => setStageModal({ ...stageModal, show: false })}
            type={stageModal.type}
            notes={stageModal.notes}
            setNotes={(n) => setStageModal({ ...stageModal, notes: n })}
            error={stageError}
            setError={setStageError}
            onConfirm={async () => {
                if (stageModal.type === 'Reassign') {
                    const success = await handleClarificationRequest(activeSection, stageModal.notes);
                    if (success) setStageModal({ ...stageModal, show: false });
                } else {
                    executeStageTransition(stageModal.type === 'Accept' ? 'Submitted' : 'Rejected', stageModal.notes);
                }
            }}
            isUpdating={isStageUpdating}
        />

        <AIImprovementModal
            isOpen={showAIModal}
            onClose={() => setShowAIModal(false)}
            aiPrompt={aiPrompt}
            setAiPrompt={setAiPrompt}
            onRefine={async () => { await handleAIImprove(); setShowAIModal(false); }}
            isRefining={isRefining}
        />

        <RenameVersionModal
            show={showLabelModal}
            onClose={() => setShowLabelModal(false)}
            versionLabelText={versionLabelText}
            setVersionLabelText={setVersionLabelText}
            handleRenameVersion={handleRenameVersion}
        />

        <RevertVersionModal
            show={showRevertModal}
            onClose={() => setShowRevertModal(false)}
            revertTargetVersion={revertTargetVersion}
            revertReason={revertReason}
            setRevertReason={setRevertReason}
            handleRevertVersion={handleRevertVersion}
            stripPrefix={stripPrefix}
            activeSection={activeSection}
        />

        <DecisionModal
            isOpen={isDecisionModalOpen}
            onClose={() => setIsDecisionModalOpen(false)}
            decisionSection={decisionSection}
            decisionDate={decisionDate}
            setDecisionDate={setDecisionDate}
            isUpdating={isUpdating}
            handleExtensionDecision={handleExtensionDecision}
            toDMY={toDMY}
        />

        <ExtensionRequestModal
            isOpen={isExtensionModalOpen}
            onClose={() => setIsExtensionModalOpen(false)}
            selectedSection={selectedSection}
            editableData={editableData}
            extensionDate={extensionDate}
            setExtensionDate={setExtensionDate}
            extensionNotes={extensionNotes}
            setExtensionNotes={setExtensionNotes}
            isUpdating={isUpdating}
            handleRequestExtension={handleRequestExtension}
            toDMY={toDMY}
        />
    </>
);

export default RFPModalsGroup;
